/** Search parameter that carries the side panel stack: `?panel=task:123&panel=user:456` (bottom → top). */
export const PANEL_PARAM = 'panel';

export interface PanelEntry {
  type: string;
  id: string;
  /** Stable identity: `type:id`. */
  key: string;
}

const TYPE_PATTERN = /^[a-z][a-z0-9-]*$/u;

export function parsePanelStack(search: string): PanelEntry[] {
  const params = new URLSearchParams(search);
  const seen = new Set<string>();
  const stack: PanelEntry[] = [];
  for (const raw of params.getAll(PANEL_PARAM)) {
    const separator = raw.indexOf(':');
    if (separator <= 0) continue;
    const type = raw.slice(0, separator);
    const id = raw.slice(separator + 1);
    if (!TYPE_PATTERN.test(type) || id.length === 0) continue;
    const key = `${type}:${id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    stack.push({ type, id, key });
  }
  return stack;
}

/**
 * Returns the search string (with leading `?`, or empty) for `stack`, keeping unrelated parameters.
 * Panel entries keep `:` unescaped for readable URLs (it is valid in a query component).
 */
export function serializePanelStack(
  search: string,
  stack: readonly Pick<PanelEntry, 'type' | 'id'>[],
): string {
  const params = new URLSearchParams(search);
  params.delete(PANEL_PARAM);
  const rest = params.toString();
  const panels = stack.map((e) => `${PANEL_PARAM}=${encodeURIComponent(e.type)}:${encodeURIComponent(e.id)}`);
  const query = [rest, ...panels].filter(Boolean).join('&');
  return query ? `?${query}` : '';
}
